import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, DollarSign, Users, AlertTriangle, TrendingUp, Loader2, RefreshCw, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PaymentStats {
  active_count: number;
  past_due_count: number;
  canceled_count: number;
  mrr: number;
  available_balance: number;
  currency: string;
}

interface Subscriber {
  subscription_id: string;
  customer_id: string;
  email: string;
  name: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_start: string;
  current_period_end: string;
  canceled_at: string | null;
  created: string;
  amount: number;
  currency: string;
}

const statusLabels: Record<string, { label: string; className: string }> = {
  active: { label: "Activa", className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  past_due: { label: "Pago pendiente", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  canceled: { label: "Cancelada", className: "bg-red-500/20 text-red-400 border-red-500/30" },
  trialing: { label: "Prueba", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  unpaid: { label: "Impaga", className: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const PaymentsDashboardPage = () => {
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery<PaymentStats>({
    queryKey: ['admin_payment_stats'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-payments', {
        body: { action: 'stats' },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: subscribersData, isLoading: subsLoading, refetch: refetchSubs } = useQuery<{ subscribers: Subscriber[] }>({
    queryKey: ['admin_subscribers'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-payments', {
        body: { action: 'subscribers' },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const subscribers = subscribersData?.subscribers || [];

  // Search, filter, pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subscribers.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      return (
        s.email?.toLowerCase().includes(q) ||
        s.name?.toLowerCase().includes(q) ||
        s.customer_id?.toLowerCase().includes(q) ||
        s.subscription_id?.toLowerCase().includes(q)
      );
    });
  }, [subscribers, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  const resetPage = () => setPage(1);

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatDate = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gestión de Pagos</h1>
          <p className="text-muted-foreground">Estadísticas y gestión de suscripciones</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { refetchStats(); refetchSubs(); }}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">MRR</p>
                <p className="text-2xl font-bold text-foreground">
                  {statsLoading ? '...' : formatCurrency(stats?.mrr || 0, stats?.currency)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Suscriptores activos</p>
                <p className="text-2xl font-bold text-foreground">
                  {statsLoading ? '...' : stats?.active_count || 0}
                </p>
              </div>
              <Users className="w-8 h-8 text-emerald-400/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pagos pendientes</p>
                <p className="text-2xl font-bold text-foreground">
                  {statsLoading ? '...' : stats?.past_due_count || 0}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Balance disponible</p>
                <p className="text-2xl font-bold text-foreground">
                  {statsLoading ? '...' : formatCurrency(stats?.available_balance || 0, stats?.currency)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscribers table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Suscriptores ({filtered.length}{filtered.length !== subscribers.length ? ` de ${subscribers.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por email, nombre o ID..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activa</SelectItem>
                <SelectItem value="trialing">Prueba</SelectItem>
                <SelectItem value="past_due">Pago pendiente</SelectItem>
                <SelectItem value="unpaid">Impaga</SelectItem>
                <SelectItem value="canceled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); resetPage(); }}>
              <SelectTrigger className="w-full md:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / pág</SelectItem>
                <SelectItem value="25">25 / pág</SelectItem>
                <SelectItem value="50">50 / pág</SelectItem>
                <SelectItem value="100">100 / pág</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {subsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {subscribers.length === 0 ? 'No hay suscriptores aún.' : 'No hay resultados con los filtros aplicados.'}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Próxima renovación</TableHead>
                      <TableHead>Cancelada el</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((sub) => {
                      const st = statusLabels[sub.status] || { label: sub.status, className: "bg-muted text-muted-foreground" };
                      return (
                        <TableRow key={sub.subscription_id}>
                          <TableCell className="font-medium">{sub.email}</TableCell>
                          <TableCell>{sub.name || '—'}</TableCell>
                          <TableCell>
                            <Badge className={st.className}>{st.label}</Badge>
                            {sub.cancel_at_period_end && (
                              <Badge className="ml-1 bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                                Cancela al final
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatCurrency(sub.amount, sub.currency)}</TableCell>
                          <TableCell>{formatDate(sub.created)}</TableCell>
                          <TableCell>{formatDate(sub.current_period_end)}</TableCell>
                          <TableCell>{formatDate(sub.canceled_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  Mostrando {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsDashboardPage;
